import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { showAlertModal } from "../lib/custom_confirm"

// Generic sortable controller for drag-and-drop reordering
// Connects to data-controller="sortable"
export default class extends Controller {
  static values = {
    url: String,        // URL to POST reordering to
    param: String,      // Parameter name for IDs array (e.g., "faq_section_ids")
    handle: String,     // CSS selector for drag handle (optional)
    draggable: String   // CSS selector limiting which children are sortable (optional)
  }

  connect() {
    this.sortable = new Sortable(this.element, {
      animation: 150,
      handle: this.hasHandleValue ? this.handleValue : null,
      ...(this.hasDraggableValue ? { draggable: this.draggableValue } : {}),
      onEnd: this.end.bind(this)
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  async end(event) {
    // Companion rows (data-child-of) travel with their parent, not on their
    // own — Sortable moved only the parent, so re-seat children before reading
    // the order.
    this.rehomeChildRows()

    // Direct children only: an expanded companion row can contain a whole
    // nested sortable list whose [data-id] rows must not leak into THIS
    // list's payload. (children-filter instead of ':scope >' — jest's jsdom
    // selector engine mishandles :scope.)
    const ids = this.directChildren('[data-id]').map(item => item.dataset.id)

    // Get CSRF token
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    this.setSaving(true)
    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({ [this.paramValue]: ids })
      })

      if (!response.ok) {
        let errorMessage = 'Failed to reorder items'
        try {
          const data = await response.json()
          errorMessage = data.error || errorMessage
        } catch {
          // Server returned non-JSON response (e.g., HTML error page)
          errorMessage = `Server error (${response.status})`
        }
        throw new Error(errorMessage)
      }

      // Update position numbers in the UI if present
      this.updatePositionNumbers()
    } catch (error) {
      console.error('Reorder error:', error.message)
      await showAlertModal('Failed to reorder items: ' + error.message, { variant: 'danger' })
      location.reload()
    } finally {
      this.setSaving(false)
    }
  }

  // While the reorder PATCH is in flight, dim the list and block further
  // drags/clicks — without this, a slow save gives zero feedback and invites
  // a second drag that races the first.
  setSaving(saving) {
    this.sortable.option("disabled", saving)
    this.element.classList.toggle("opacity-50", saving)
    this.element.classList.toggle("pe-none", saving)
    this.element.setAttribute("aria-busy", String(saving))
  }

  updatePositionNumbers() {
    this.directChildren('[data-id]').forEach((item, index) => {
      const positionCell = item.querySelector('[data-position]')
      if (positionCell) {
        positionCell.textContent = index + 1
      }
    })
  }

  // Keep each data-child-of row directly after its parent [data-id] row.
  // Child rows are excluded from dragging (via the draggable selector), so a
  // parent dragged past one leaves it stranded until this runs.
  rehomeChildRows() {
    this.directChildren('[data-child-of]').forEach((child) => {
      const parent = this.directChildren(`[data-id="${CSS.escape(child.dataset.childOf)}"]`)[0]
      if (parent && parent.nextElementSibling !== child) {
        parent.after(child)
      }
    })
  }

  directChildren(selector) {
    return Array.from(this.element.children).filter((el) => el.matches(selector))
  }
}
